import { ownFootprintIssues } from "../compile/prop-footprint.ts";
import type { CompileContext } from "../compile/context.ts";
import { resolveMap } from "../compile/resolve-map.ts";
import { downsampleGrid } from "../document/grid.ts";
import { COLLISION_CELLS_PER_TILE, COMPILED_COLLISION_CELLS_PER_TILE, type MapDocument, type TileRef, type WorldDocument } from "../document/types.ts";
import type { TerrainFaceIssue } from "../terrain/faces.ts";
import type { TerrainFace } from "../terrain/types.ts";
import { registerAdapter, registeredEntityTypes, type DaedalusGameAdapter, type ValidationIssue } from "./adapter.ts";
import { behaviourIssues } from "./behaviour.ts";
import { cellSourceIssues, contextFrameSize, lowerCellSources, type LoweredCells } from "./cell-sources.ts";
import { faceHeightIssues } from "./face-height.ts";
import { missingTerrainIssues } from "./terrain-missing-issues.ts";
import { missingFrameIssues } from "./missing-frame-issues.ts";
import { lookupIssues, type LookupList } from "./lookups.ts";
import { cellAlignmentIssues, contextPropMeasure } from "./prop-align-issues.ts";
import { lostPullIssues } from "./pull-issues.ts";
import { mapReferences, referenceIssues, type ReferenceLink } from "./references.ts";
import { SessionManager } from "./sessions.ts";
import { zoneIssues } from "./zone-overlay.ts";
import { doorIssues } from "./door-issues.ts";
import { draftPlacementIssues, studioTileIssues } from "./draft-issues.ts";
import { lostEditIssues } from "./lost-edit-issues.ts";
import type { EditorCatalogue } from "./catalogue.ts";
import { mapLinkIssues } from "./door-links.ts";
import { seamIssues } from "./seam-issues.ts";
import { frameId } from "../assets/manifest.ts";
import { frameSizeOf } from "../compile/behaviour.ts";

/**
 * The editor's validation of one map as a pure function (ADR-099 decision 22): the behaviour issues (ADR-053),
 * the broken references, the face heights, the zone types, the door fields, the links of the map's doors, host and
 * arrival points (ADR-122 decision 2.5, warnings at the save), the followed zones and the lookups Daedalus checks,
 * a warning for each prop off the collision cells, one for the props whose frame the context cannot size, then
 * what the adapter's `validate` hook reports of the map with its followed zones lowered to cells, handed the world,
 * a warning for each pulled frame its pack no longer
 * holds, a warning for each draft of a studio pack the map places, and the seam checks against its placed neighbours (`seamIssues`, ADR-109 decision 6). `EditorStore.validate()` and `validateMap` both compose
 * `composeValidation`, the store from what it keeps and `validateMap` from the document, the compile context and
 * the adapter alone, so a reader with no open editor lists the Issues panel's issues.
 */

/**
 * What one map's validation reads.
 */
export interface ValidationParts {
  readonly world: WorldDocument;
  readonly map: MapDocument;
  readonly tileSize: number;
  readonly context: CompileContext;
  /** The adapter as `registerAdapter` admitted it. */
  readonly adapter: DaedalusGameAdapter;
  /** A terrain's face as the editor uses it: absent when unknown, undeclared or malformed. */
  readonly faceOf: (terrainId: string) => TerrainFace | undefined;
  /** The context's malformed face declarations. */
  readonly malformedFaces: readonly TerrainFaceIssue[];
  /** The map with every followed zone lowered to cells. */
  readonly lowered: LoweredCells;
  /** The map's typed references. */
  readonly links: readonly ReferenceLink[];
  /** A lookup list by name, or null while it is unknown. */
  readonly lookup: (name: string) => LookupList | null;
  /** The map's effective collision grid, one byte an authoring cell, asked for at most once a check. */
  readonly collision: () => Uint8Array;
  /** The pulled frames the last compile found their pack lacks, as the editor's catalogue names them. */
  readonly lostPulls?: readonly TileRef[] | undefined;
  /** The editor's catalogue, for the drafts it marks (ADR-107 decision 31); none are known unless given. */
  readonly catalogue?: EditorCatalogue | undefined;
}

/**
 * Composes the checks over one map.
 *
 * @param parts What the checks read.
 * @returns The issues, in the Issues panel's order.
 */
export function composeValidation(parts: ValidationParts): readonly ValidationIssue[] {
  const { map, context, adapter, tileSize, lowered } = parts;
  const objectTypes = adapter.objectTypes ?? [];
  const types = { objectTypes, entityTypes: registeredEntityTypes(adapter) };
  const zoneTypes = adapter.zoneTypes ?? [];
  const grid = { tileSize, cellsPerTile: COLLISION_CELLS_PER_TILE };
  const faces = [...missingTerrainIssues(map, context.terrains), ...faceHeightIssues(map, context.terrains, parts.faceOf, parts.malformedFaces)];
  const sources = cellSourceIssues(map, lowered.followed, types, zoneTypes);
  const lookups = lookupIssues(map, types, parts.lookup);
  const offCells = cellAlignmentIssues(map, contextPropMeasure(context, grid), grid);
  let collision: Uint8Array | null = null;
  const width = map.width * COLLISION_CELLS_PER_TILE;
  const blocked = (_map: MapDocument, column: number, row: number): boolean => {
    collision ??= parts.collision();
    return (collision[row * width + column] ?? 0) !== 0;
  };
  const links = mapLinkIssues(parts.world, map, { ...(adapter.arrivalType === undefined ? {} : { arrivalType: adapter.arrivalType }), blocked });
  return [...behaviourIssues(map, objectTypes, context, tileSize), ...referenceIssues(map, types, parts.links), ...faces, ...zoneIssues(map, zoneTypes), ...doorIssues(map, adapter.doorFields), ...links, ...sources, ...lookups, ...offCells, ...ownFootprintIssues(map, context, tileSize), ...missingFrameIssues(map, context), ...(adapter.validate?.(lowered.map, { tileSize, context, world: parts.world }) ?? []), ...lostPullIssues(parts.world, map.id, parts.lostPulls), ...draftPlacementIssues(map, parts.catalogue), ...lostEditIssues(map.id, parts.catalogue?.lostEdits), ...studioTileIssues(parts.world, map), ...seamIssues({ world: parts.world, map, tileSize, adapter, sizeOf: (frame) => frameSizeOf(context, frameId(frame.tileset, null, frame.tile)), terrains: context.terrains })];
}

/**
 * Validates one map of a world as the editor's Issues panel would.
 *
 * @param document The world.
 * @param mapId The map.
 * @param context The world's compile context.
 * @param adapter The game's adapter.
 * @param lookup A lookup list by name; none is known unless given, as in an editor whose host has handed none.
 * @returns The issues.
 * @throws Error when the world holds no such map, or the adapter does not register, or, for a map holding an arrival
 *   point, the map does not resolve (`resolveMap`).
 */
export function validateMap(document: WorldDocument, mapId: string, context: CompileContext, adapter: DaedalusGameAdapter, lookup: (name: string) => LookupList | null = () => null): readonly ValidationIssue[] {
  const map = document.maps.find((candidate) => candidate.id === mapId);
  if (map === undefined) {
    throw new Error(`the world holds no map ${mapId}`);
  }
  const registered = registerAdapter(adapter);
  const sessions = new SessionManager(context);
  const types = { objectTypes: registered.objectTypes ?? [], entityTypes: registeredEntityTypes(registered) };
  const lowered = lowerCellSources(map, document.tileSize, contextFrameSize(context));
  return composeValidation({
    world: document,
    map,
    tileSize: document.tileSize,
    context,
    adapter: registered,
    faceOf: (terrainId) => sessions.faceOf(terrainId),
    malformedFaces: sessions.malformedFaces,
    lowered,
    links: mapReferences(map, types),
    lookup,
    collision: () => downsampleGrid(resolveMap(document, lowered.map, context).collision, lowered.map.width * COMPILED_COLLISION_CELLS_PER_TILE, lowered.map.height * COMPILED_COLLISION_CELLS_PER_TILE, COMPILED_COLLISION_CELLS_PER_TILE / COLLISION_CELLS_PER_TILE),
  });
}
